import React, { useCallback, useEffect, useMemo, useState } from 'react';
import "./i18n"
import './App.css';
import {
  Routes,
  Route,
  useNavigate
} from "react-router-dom";
import RecipeOverview from './components/recipe-overview/RecipeOverview';
import Menu from './components/menu/Menu';
import {
  AppBar, CssBaseline, IconButton, InputBase, ThemeProvider,
  Toolbar, alpha, createTheme, styled, useMediaQuery
} from '@mui/material';
import MenuIcon from '@mui/icons-material/Menu';
import AccountSettings from './components/account/AccountSettings';
import RecipeDetails from './components/recipe/RecipeDetails';
import RecipeEditor from './components/recipe/edit/RecipeEdit';
import { getCurrentUser } from './utils/AccountService';
import { User } from './models/User';
import { useTranslation } from 'react-i18next';
import Register from './components/account/Register';
import Login from './components/account/Login';
import RequireAuth from './components/account/RequireAuth';
import SearchIcon from '@mui/icons-material/Search';
import HomeIcon from '@mui/icons-material/Home';
import Cookies from 'js-cookie';
import { ShoppingList } from "./components/shopping-list/ShoppingList";
import MealPlanner from "./components/meal-plan/MealPlanner";
import RecipeMatch from "./components/recipe-match/RecipeMatch";
import RecipeImport from "./components/recipe/import/RecipeImport";

const Search = styled('div')(({ theme }) => ({
  position: 'relative',
  borderRadius: 24,
  backgroundColor: alpha(theme.palette.common.white, 0.18),
  border: `1px solid ${alpha(theme.palette.common.white, 0.3)}`,
  '&:hover': {
    backgroundColor: alpha(theme.palette.common.white, 0.26),
  },
  marginLeft: 0,
  width: '100%',
  [theme.breakpoints.up('sm')]: {
    marginLeft: theme.spacing(1),
    width: 'auto',
  },
  transition: 'background-color 0.2s',
}));

const SearchIconWrapper = styled('div')(({ theme }) => ({
  padding: theme.spacing(0, 2),
  height: '100%',
  position: 'absolute',
  pointerEvents: 'none',
  display: 'flex',
  alignItems: 'center',
  justifyContent: 'center',
  opacity: 0.85,
}));

const StyledInputBase = styled(InputBase)(({ theme }) => ({
  color: 'inherit',
  width: '100%',
  '& .MuiInputBase-input': {
    padding: theme.spacing(0.9, 1, 0.9, 0),
    paddingLeft: `calc(1em + ${theme.spacing(4)})`,
    transition: theme.transitions.create('width'),
    fontSize: '0.9rem',
    [theme.breakpoints.up('sm')]: {
      width: '28ch',
      '&:focus': {
        width: '36ch',
      },
    },
  },
}));

function buildWarmTheme(mode: 'light' | 'dark', fontSize: number) {
  const isLight = mode === 'light';
  return createTheme({
    palette: {
      mode,
      primary: {
        main: isLight ? '#D97706' : '#F59E0B',
        light: '#FCD34D',
        dark: '#92400E',
        contrastText: '#FFFFFF',
      },
      secondary: {
        main: '#92400E',
        light: '#B45309',
        dark: '#78350F',
        contrastText: '#FFFFFF',
      },
      background: {
        default: isLight ? '#FFF8F3' : '#1C1208',
        paper: isLight ? '#FFFCF8' : '#261910',
      },
      text: {
        primary: isLight ? '#292524' : '#FEF3C7',
        secondary: isLight ? '#78716C' : '#C4A882',
      },
      divider: isLight ? '#F0E4D4' : '#3D2A15',
      error: { main: '#DC2626' },
      success: { main: '#16A34A' },
      warning: { main: '#D97706' },
    },
    typography: {
      fontFamily: "'Inter', 'Segoe UI', system-ui, sans-serif",
      fontSize,
      button: {
        fontWeight: 600,
        textTransform: 'none',
      },
    },
    shape: {
      borderRadius: 12,
    },
    components: {
      MuiCssBaseline: {
        styleOverrides: {
          body: {
            backgroundColor: isLight ? '#FFF8F3' : '#1C1208',
          },
        },
      },
      MuiAppBar: {
        styleOverrides: {
          root: {
            backgroundColor: isLight ? '#C2680A' : '#1C1208',
            backgroundImage: isLight
              ? 'linear-gradient(135deg, #D97706 0%, #B45309 100%)'
              : 'none',
            boxShadow: isLight
              ? '0 2px 16px rgba(180, 83, 9, 0.3)'
              : '0 2px 16px rgba(0,0,0,0.5)',
          },
        },
      },
      MuiPaper: {
        styleOverrides: {
          root: {
            backgroundImage: 'none',
          },
          elevation2: {
            boxShadow: isLight
              ? '0 2px 8px rgba(180, 83, 9, 0.09), 0 0 0 1px rgba(224, 189, 140, 0.35)'
              : '0 2px 8px rgba(0,0,0,0.35)',
          },
          elevation3: {
            boxShadow: isLight
              ? '0 4px 16px rgba(180, 83, 9, 0.12)'
              : '0 4px 16px rgba(0,0,0,0.45)',
          },
          elevation4: {
            boxShadow: isLight
              ? '0 6px 20px rgba(180, 83, 9, 0.15)'
              : '0 6px 20px rgba(0,0,0,0.5)',
          },
        },
      },
      MuiDrawer: {
        styleOverrides: {
          paper: {
            backgroundColor: isLight ? '#FFFCF8' : '#1E1208',
            borderRight: 'none',
            boxShadow: isLight
              ? '4px 0 24px rgba(180, 83, 9, 0.14)'
              : '4px 0 24px rgba(0,0,0,0.5)',
          },
        },
      },
      MuiListItemButton: {
        styleOverrides: {
          root: {
            borderRadius: 10,
            margin: '2px 8px',
            '&.Mui-selected': {
              backgroundColor: isLight ? 'rgba(217, 119, 6, 0.12)' : 'rgba(245, 158, 11, 0.15)',
              color: isLight ? '#B45309' : '#F59E0B',
              '&:hover': {
                backgroundColor: isLight ? 'rgba(217, 119, 6, 0.18)' : 'rgba(245, 158, 11, 0.22)',
              },
            },
            '&:hover': {
              backgroundColor: isLight ? 'rgba(217, 119, 6, 0.07)' : 'rgba(245, 158, 11, 0.08)',
            },
          },
        },
      },
      MuiListItemIcon: {
        styleOverrides: {
          root: {
            minWidth: 40,
          },
        },
      },
      MuiButton: {
        styleOverrides: {
          root: {
            borderRadius: 10,
            fontWeight: 600,
            textTransform: 'none',
            padding: '8px 18px',
          },
          contained: {
            boxShadow: '0 2px 8px rgba(180, 83, 9, 0.22)',
            '&:hover': {
              boxShadow: '0 4px 14px rgba(180, 83, 9, 0.32)',
            },
          },
          outlined: {
            borderWidth: 1.5,
            '&:hover': {
              borderWidth: 1.5,
            },
          },
        },
      },
      MuiChip: {
        styleOverrides: {
          root: {
            borderRadius: 8,
            fontWeight: 500,
          },
        },
      },
      MuiBottomNavigation: {
        styleOverrides: {
          root: {
            backgroundColor: isLight ? '#FFFCF8' : '#1E1208',
            borderTop: `1px solid ${isLight ? '#F0E4D4' : '#3D2A15'}`,
            height: 60,
          },
        },
      },
      MuiBottomNavigationAction: {
        styleOverrides: {
          root: {
            color: isLight ? '#A8917E' : '#8C7A5E',
            '&.Mui-selected': {
              color: isLight ? '#D97706' : '#F59E0B',
            },
            '& .MuiBottomNavigationAction-label': {
              fontSize: '0.7rem',
              fontWeight: 600,
            },
          },
        },
      },
      MuiFab: {
        styleOverrides: {
          root: {
            backgroundColor: isLight ? '#D97706' : '#F59E0B',
            color: '#FFFFFF',
            boxShadow: '0 6px 20px rgba(180, 83, 9, 0.4)',
            '&:hover': {
              backgroundColor: isLight ? '#B45309' : '#D97706',
              boxShadow: '0 8px 24px rgba(180, 83, 9, 0.5)',
            },
          },
          primary: {
            backgroundColor: isLight ? '#D97706' : '#F59E0B',
            color: '#FFFFFF',
          },
        },
      },
      MuiSpeedDial: {
        styleOverrides: {
          fab: {
            backgroundColor: isLight ? '#D97706' : '#F59E0B',
            color: '#FFFFFF',
            boxShadow: '0 6px 20px rgba(180, 83, 9, 0.4)',
            '&:hover': {
              backgroundColor: isLight ? '#B45309' : '#D97706',
            },
          },
        },
      },
      MuiSpeedDialAction: {
        styleOverrides: {
          fab: {
            backgroundColor: isLight ? '#FFFCF8' : '#2A1810',
            boxShadow: isLight
              ? '0 2px 8px rgba(180, 83, 9, 0.14)'
              : '0 2px 8px rgba(0,0,0,0.4)',
          },
        },
      },
      MuiTextField: {
        styleOverrides: {
          root: {
            '& .MuiOutlinedInput-root': {
              borderRadius: 10,
            },
          },
        },
      },
      MuiOutlinedInput: {
        styleOverrides: {
          root: {
            borderRadius: 10,
          },
        },
      },
      MuiSelect: {
        styleOverrides: {
          outlined: {
            borderRadius: 10,
          },
        },
      },
      MuiDialog: {
        styleOverrides: {
          paper: {
            borderRadius: 18,
            boxShadow: isLight
              ? '0 24px 48px rgba(180, 83, 9, 0.2)'
              : '0 24px 48px rgba(0,0,0,0.7)',
          },
        },
      },
      MuiDialogTitle: {
        styleOverrides: {
          root: {
            fontWeight: 700,
          },
        },
      },
      MuiToggleButton: {
        styleOverrides: {
          root: {
            borderRadius: 8,
            textTransform: 'none',
            fontWeight: 500,
            '&.Mui-selected': {
              backgroundColor: isLight ? 'rgba(217, 119, 6, 0.14)' : 'rgba(245, 158, 11, 0.18)',
              color: isLight ? '#B45309' : '#F59E0B',
            },
          },
        },
      },
      MuiTooltip: {
        styleOverrides: {
          tooltip: {
            borderRadius: 8,
            fontSize: '0.75rem',
          },
        },
      },
    },
  });
}

function App() {
  const [menuOpen, setMenuOpen] = useState(false);
  const [userColorMode, setUserColorMode] = useState<'light' | 'dark' | null>(null);
  const [language, setLanguage] = useState('en');
  const [fontSize, setFontSize] = useState<number>(14);
  const [searchFilter, setSearchFilter] = useState('');

  const navigate = useNavigate();
  const { i18n } = useTranslation();
  const systemPrefersDark = useMediaQuery('(prefers-color-scheme: dark)');

  const isDark = userColorMode !== null ? userColorMode === 'dark' : systemPrefersDark;
  const theme = useMemo(() => buildWarmTheme(isDark ? 'dark' : 'light', fontSize), [isDark, fontSize]);

  const switchMode = useCallback((colorMode: string) => {
    setUserColorMode(colorMode as 'light' | 'dark');
  }, []);

  useEffect(() => {
    i18n.changeLanguage(language);
  }, [i18n, language]);

  const switchLanguage = useCallback((newLanguage: string) => {
    setLanguage(newLanguage);
  }, []);

  const changeFontSize = useCallback((fs: number) => {
    setFontSize(fs);
  }, []);

  const loadCurrentUser = useCallback(() => {
    const token: string | undefined = Cookies.get('token');
    if (token) {
      getCurrentUser().then((response: User) => {
        if (response !== null && response !== undefined) {
          switchMode(response.userSettings.colorMode);
          switchLanguage(response.userSettings.language);
        }
      }).catch(() => {});
    }
  }, [switchLanguage, switchMode]);

  useEffect(() => {
    loadCurrentUser();
  }, [loadCurrentUser]);

  const navigateHome = useCallback(() => {
    setSearchFilter('');
    navigate('/');
  }, [navigate]);

  return (
    <ThemeProvider theme={theme}>
      <CssBaseline />
      <main>
        <AppBar color='primary' position="fixed" elevation={0}>
          <Toolbar sx={{ gap: 1 }}>
            <IconButton size='large' edge='start' color='inherit' aria-label='menu' sx={{ mr: 0.5 }} onClick={() => setMenuOpen(true)}>
              <MenuIcon />
            </IconButton>
            <IconButton size='large' color='inherit' aria-label='home' onClick={navigateHome}>
              <HomeIcon />
            </IconButton>

            <Search sx={{ flexGrow: 1, maxWidth: 480 }}>
              <SearchIconWrapper>
                <SearchIcon fontSize="small" />
              </SearchIconWrapper>
              <StyledInputBase
                placeholder="Search recipes…"
                inputProps={{ 'aria-label': 'search' }}
                onChange={(e) => setSearchFilter(e.target.value)}
                value={searchFilter}
              />
            </Search>
          </Toolbar>
        </AppBar>

        <Menu open={menuOpen} onClose={() => setMenuOpen(false)} />

        <Toolbar />
        <Routes>
          <Route path="/" element={<RequireAuth><RecipeOverview filter={searchFilter} /></RequireAuth>} />
          <Route path="/recipe/:recipeId" element={<RequireAuth><RecipeDetails /></RequireAuth>} />
          <Route path="/recipe/edit/:recipeId" element={<RequireAuth><RecipeEditor /></RequireAuth>} />
          <Route path="/import" element={<RequireAuth><RecipeImport /></RequireAuth>} />
          <Route path="/recipePlan" element={<RequireAuth><MealPlanner /></RequireAuth>} />
          <Route path="/shoppingList" element={<RequireAuth><ShoppingList /></RequireAuth>} />
          <Route path="/match" element={<RequireAuth><RecipeMatch /></RequireAuth>} />
          <Route path="/account" element={<RequireAuth><AccountSettings switchColorMode={switchMode} changeLanguage={switchLanguage} changeFontSize={changeFontSize} /></RequireAuth>} />
          <Route path="/login" element={<Login onLoginSuccessful={loadCurrentUser} />} />
          <Route path="/register" element={<Register onLoginSuccessful={loadCurrentUser} />} />
        </Routes>
      </main>
    </ThemeProvider>
  );
}

export default App;
